// src/pages/approval/TypeList.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Pencil, Trash2, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import * as approvalTypeApi from "../../api/approval/type";

export default function ApprovalTypeList() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const loadList = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await approvalTypeApi.getTypeList(projectId);
      setRows(res || []);
    } catch (err) {
      console.error("결재 문서 유형 목록 불러오기 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, [projectId]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("삭제하시겠습니까?")) return;
    try {
      await approvalTypeApi.deleteType(projectId, id);
      await loadList();
    } catch (err) {
      alert("삭제 실패");
    }
  };

  // 검색 + 페이지네이션
  const filtered = rows.filter(
    (r) =>
      r.name?.toLowerCase().includes(search.toLowerCase()) ||
      r.title?.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const startIndex = (page - 1) * pageSize;
  const paged = filtered.slice(startIndex, startIndex + pageSize);

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="mx-auto max-w-lg px-4 pt-6 pb-28">
        {/* 헤더 */}
        <header className="mb-5">
          <div className="flex items-center justify-between">
            <h1 className="text-[28px] font-bold tracking-tight text-slate-900">
              문서양식 관리
            </h1>
            <button
              onClick={() => navigate(`/project/${projectId}/approval/type/new`)}
              className="flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-[13px] font-semibold text-white shadow-sm active:scale-[0.97] transition-transform"
            >
              <Plus size={16} strokeWidth={2.5} />
              새 양식
            </button>
          </div>
          <p className="mt-1 text-[15px] text-slate-500">
            총 {filtered.length}개의 양식
          </p>
        </header>

        {/* 검색 */}
        <section className="mb-4">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="양식명 검색"
            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-900 placeholder-slate-400 shadow-sm outline-none focus:border-slate-400"
          />
        </section>

        {/* 목록 */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <p className="text-[15px] text-slate-400">불러오는 중...</p>
          </div>
        ) : paged.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FileText size={48} className="text-slate-200 mb-4" />
            <p className="text-[15px] text-slate-400">등록된 양식이 없습니다.</p>
          </div>
        ) : (
          <section className="space-y-2">
            {paged.map((row) => (
              <div
                key={row.id}
                onClick={() =>
                  navigate(`/project/${projectId}/approval/request/new`, {
                    state: { selectedType: row },
                  })
                }
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm active:scale-[0.98] transition-transform cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-semibold text-slate-900">
                      {row.name}
                    </p>
                    <div className="mt-1 flex items-center gap-2 text-[13px] text-slate-500">
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-[12px] font-medium text-slate-600">
                        {row.code || "-"}
                      </span>
                      {row.title && (
                        <>
                          <span className="text-slate-300">·</span>
                          <span className="truncate">{row.title}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* 액션 버튼 */}
                  <div className="flex items-center gap-1 ml-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/project/${projectId}/approval/type/${row.id}`);
                      }}
                      className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-600 active:scale-[0.95]"
                      title="편집"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, row.id)}
                      className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-600 active:scale-[0.95]"
                      title="삭제"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* 페이지네이션 */}
        {!loading && paged.length > 0 && totalPages > 1 && (
          <footer className="mt-6 flex items-center justify-center gap-4">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-600 shadow-sm disabled:opacity-40 active:scale-[0.95]"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex items-center gap-1 text-[14px]">
              <span className="font-semibold text-slate-900">{page}</span>
              <span className="text-slate-400">/</span>
              <span className="text-slate-500">{totalPages}</span>
            </div>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-600 shadow-sm disabled:opacity-40 active:scale-[0.95]"
            >
              <ChevronRight size={20} />
            </button>
          </footer>
        )}
      </main>
    </div>
  );
}
